import React, {
  createContext, useContext, useState, useEffect, useCallback, useMemo,
} from 'react';
import type { IPreferenceService } from '@bingads-webui/preference-service';
import { PreferenceService } from '@bingads-webui/preference-service';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { UNIVERSAL_CAMPAIGN_EXPERIENCE_ENABLED_KEY } from './constants';
import type { PreferenceUtilityPermissions } from './types';

export interface IUCExperienceToggleContextValue {
  isUCExperienceEnabled: boolean;
  setUCExperienceEnabled: (enabled: boolean) => void;
}

const defaultContextValue: IUCExperienceToggleContextValue = {
  isUCExperienceEnabled: false,
  setUCExperienceEnabled: () => {},
};

export const UCExperienceToggleContext =
  createContext<IUCExperienceToggleContextValue>(defaultContextValue);

UCExperienceToggleContext.displayName = 'UCExperienceToggleContext';

const hasUCPermission = (permissions?: PreferenceUtilityPermissions): boolean =>
  !!permissions?.dynamic?.UniversalCampaign;

const getUCEnabledFromPreference = (permissions?: PreferenceUtilityPermissions): boolean => {
  const preference = PreferenceService.findByNameAtUserLevel(UNIVERSAL_CAMPAIGN_EXPERIENCE_ENABLED_KEY);
  return hasUCPermission(permissions) && !!(preference?.Data);
};

export const isUCExperienceEnabled = (permissions?: PreferenceUtilityPermissions): boolean =>
  getUCEnabledFromPreference(permissions);

export const UCExperienceToggleProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const permissions = useContext(PermissionsContext);
  const [enabled, setEnabled] = useState<boolean>(() => getUCEnabledFromPreference(permissions));

  useEffect(() => {
    setEnabled(getUCEnabledFromPreference(permissions));
  }, [permissions]);

  const setUCExperienceEnabled = useCallback((value: boolean) => {
    (PreferenceService as IPreferenceService).setAtUserLevel!({
      Name: UNIVERSAL_CAMPAIGN_EXPERIENCE_ENABLED_KEY,
      Data: value,
    });
    setEnabled(value && hasUCPermission(permissions));
  }, [permissions]);

  const contextValue = useMemo<IUCExperienceToggleContextValue>(() => ({
    isUCExperienceEnabled: enabled,
    setUCExperienceEnabled,
  }), [enabled, setUCExperienceEnabled]);

  return (
    <UCExperienceToggleContext.Provider value={contextValue}>
      {children}
    </UCExperienceToggleContext.Provider>
  );
};
